import React, { useId } from 'react'
import { LuPlus, LuMinus } from 'react-icons/lu'
import Label from './formLabel'

interface NumberInputProps {
    label?: string
    placeholder?: string
    disabled?: boolean
    allowNegative?: boolean
    allowDecimal?: boolean
    twoDecimal?: boolean
    required?: boolean
    value: number | ''
    onChange: (v: number | '') => void
    onBlur?: () => void
    min?: number | ''
    max?: number | ''
    error?: any
    control?: boolean
    information?: string
    badge?: React.ReactNode
    readonly?: boolean
    className?: string
}

const NumberInput: React.FC<NumberInputProps> = ({
    label,
    placeholder = 'Enter a number',
    disabled = false,
    allowNegative = false,
    allowDecimal = false,
    twoDecimal = false,
    required = false,
    value = '',
    onChange = (v: number | '') => {},
    onBlur = () => {},
    min = '',
    max = '',
    error,
    control = false,
    information,
    badge,
    readonly = false,
    className = `w-full border border-[#D4D4DA] h-[1.709rem] text-[0.667rem] pl-2 pr-[4.4rem] min-w-[4rem] rounded-[0.267rem] focus:outline-none disabled:bg-[#E6E7EA] disabled:border-[#D4D4DA] ${error ? 'border-2 border-red-500' : ''}`,
}) => {
    const inputId = `number-input-${useId()}`

    const getAllowedCharsRegex = (allowDecimal: boolean, allowNegative: boolean): RegExp => {
        if (allowDecimal && allowNegative) return /[^\d.-]/g
        if (allowDecimal) return /[^\d.]/g
        if (allowNegative) return /[^\d-]/g
        return /[^\d]/g
    }

    const removeDuplicateDecimals = (value: string): string => {
        if (!allowDecimal || !value.includes('.')) return value
        const decimalCount = (value.match(/\./g) || []).length
        if (decimalCount <= 1) return value
        const firstDotIndex = value.indexOf('.')
        return value.substring(0, firstDotIndex + 1) + value.substring(firstDotIndex + 1).replaceAll('.', '')
    }

    const limitDecimalPlaces = (value: string): string => {
        if (!allowDecimal || !twoDecimal) return value
        const match = /^-?\d*(?:\.\d{0,2})?/.exec(value)
        return match ? match[0] : ''
    }

    const applyMinMax = (value: string): string => {
        if (value === '' || value === '-') return value
        const num = Number(value)
        if (typeof min === 'number' && num < min) return String(min)
        if (typeof max === 'number' && num > max) return String(max)
        return value
    }

    const checkAllowDecimal = (value: any) => {
        const allowedChars = getAllowedCharsRegex(allowDecimal, allowNegative)
        value = value.replace(allowedChars, '')
        value = removeDuplicateDecimals(value)
        value = limitDecimalPlaces(value)
        value = applyMinMax(value)
        return value
    }

    const handleNumberInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange(checkAllowDecimal(e.target.value))
    }

    const handleIncrement = () => {
        if (disabled || readonly) return
        let v = value
        if (v == '') {
            v = 1
        } else {
            v = v + 1
        }
        if (typeof max == 'number') {
            onChange(Math.min(v, max))
        } else {
            onChange(v)
        }
    }

    const handleDecrement = () => {
        if (disabled || readonly) return
        let v = value
        if (v == '') {
            v = -1
        } else {
            v = v - 1
        }

        if (typeof min == 'number') {
            onChange(Math.max(v, min))
        } else {
            if (!allowNegative && v < 0) {
                v = 0
            }
            onChange(v)
        }
    }

    return (
        <div className="">
            {label && <Label htmlFor={inputId} text={label} required={required} information={information} badge={badge} />}
            <div className="relative">
                <input
                    // type="number"
                    id={inputId}
                    value={value}
                    onChange={handleNumberInputChange}
                    placeholder={placeholder}
                    disabled={disabled}
                    onBlur={onBlur}
                    min={min}
                    max={max}
                    readOnly={readonly}
                    className={className}
                />
                {control && (
                    <div className="absolute top-0 right-0 h-full flex items-center gap-2 p-2 text-[1.2rem] ">
                        <LuMinus className="cursor-pointer" onClick={handleDecrement} data-testid="minus-icon" />
                        <div className="h-full border-r border-[#d4d4d4]" />
                        <LuPlus className="cursor-pointer" onClick={handleIncrement} data-testid="plus-icon" />
                    </div>
                )}
            </div>
            {error && <p className="text-error text-[.58rem] mt-1">{error}</p>}
        </div>
    )
}

export default NumberInput
